/**
 * SoleadLab 2.0 — FL-3a client-side photo shrink
 *
 * Re-encodes a camera/gallery photo to JPEG with a bounded long edge before
 * upload: a ~4 MB HEIC / ~12 MB JPEG becomes a few hundred KB over a cell
 * connection, and HEIC (decodable on the Apple device that produced it, but
 * not on desktop Chrome) becomes universally displayable JPEG. The re-encode
 * also strips EXIF (GPS, device serial) — a privacy win for photos that
 * view-link visitors will see. Callers needing the photo's GPS must extract
 * it from the ORIGINAL file (utils/exif.ts) BEFORE calling this.
 *
 * Fails soft: any decode/encode problem returns the original file untouched —
 * the server allowlist still accepts it (incl. HEIC), so capture never hard-
 * fails on an exotic format.
 */

/** Longest output edge in px (owner-tunable on the real-phone pass). */
const MAX_EDGE_PX = 2048;
const JPEG_QUALITY = 0.85;

export interface DownscaledPhoto {
  blob: Blob;
  name: string;
}

function jpegName(originalName: string): string {
  const base = originalName.replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.jpg`;
}

export async function downscaleImageToJpeg(file: File): Promise<DownscaledPhoto> {
  let bitmap: ImageBitmap | null = null;
  try {
    try {
      // from-image applies the EXIF orientation so the canvas isn't rotated.
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Older Safari rejects the options bag — retry bare.
      bitmap = await createImageBitmap(file);
    }

    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { blob: file, name: file.name };
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob || blob.size === 0) return { blob: file, name: file.name };
    return { blob, name: jpegName(file.name) };
  } catch {
    return { blob: file, name: file.name };
  } finally {
    bitmap?.close();
  }
}
